import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  fr: {
    nav: {
      home: 'Accueil',
      services: 'Services',
      listings: 'Logements',
      news: 'Actualités',
      about: 'À propos',
      contact: 'Contact',
      login: 'Connexion Admin'
    },
    hero: {
      title: 'Votre partenaire pour un nouveau départ à Lyon',
      subtitle: 'AILE aide les étudiants internationaux à trouver un logement et à s\'intégrer facilement en France',
      cta: 'Découvrir nos services',
      ctaSecondary: 'Nous contacter'
    },
    services: {
      title: 'Nos Services',
      subtitle: 'Un accompagnement complet pour votre réussite',
      housing: {
        title: 'Recherche de Logement',
        description: 'Nous vous aidons à trouver le logement parfait adapté à vos besoins et votre budget. Studio, T1, T2 ou colocation.'
      },
      admin: {
        title: 'Accompagnement Administratif',
        description: 'Aide complète pour vos démarches : CAF, sécurité sociale, préfecture, titre de séjour. On simplifie tout pour vous.'
      },
      job: {
        title: 'Recherche de Job Étudiant',
        description: 'Accompagnement personnalisé pour trouver un job étudiant et faciliter votre indépendance financière.'
      },
      integration: {
        title: 'Faciliter l\'Intégration',
        description: 'Un processus interactif pour vous aider à vous sentir chez vous à Lyon, de l\'arrivée à la fin de vos études.'
      }
    },
    about: {
      title: 'À propos d\'AILE',
      mission: 'Notre Mission',
      missionText: 'AILE est une association à but non lucratif née en 2019 pour aider les étudiants internationaux à Lyon. Nous favorisons l\'intégration en offrant un accompagnement personnalisé dans la recherche de logement et les démarches administratives.',
      values: 'Nos Valeurs',
      proximity: 'Proximité',
      proximityText: 'Une aide adaptée à vos besoins',
      solidarity: 'Solidarité',
      solidarityText: 'Écoute active et relation forte',
      love: 'Amour',
      loveText: 'Partage et entraide',
      respect: 'Respect',
      respectText: 'Sans distinction de nationalité, religion ou politique',
      pricing: 'Tarification',
      pricingText: 'Frais d\'adhésion unique de 20€ pour un accompagnement complet à tous nos services.'
    },
    contact: {
      title: 'Contactez-nous',
      subtitle: 'Une question ? Besoin d\'aide ? Nous sommes là pour vous.',
      name: 'Nom complet',
      email: 'Email',
      phone: 'Téléphone (optionnel)',
      subject: 'Sujet',
      message: 'Votre message',
      send: 'Envoyer',
      sending: 'Envoi en cours...',
      success: 'Message envoyé avec succès !',
      error: 'Erreur lors de l\'envoi',
      info: {
        title: 'Informations de contact',
        phone: 'Téléphone',
        email: 'Email',
        address: 'Adresse',
        addressValue: '1 place Abbé Pierre, 69009 Lyon'
      }
    },
    listings: {
      title: 'Logements Disponibles',
      subtitle: 'Trouvez votre nouveau chez-vous à Lyon',
      rent: 'Loyer',
      deposit: 'Caution',
      duration: 'Durée',
      available: 'Disponible à partir de',
      features: 'Caractéristiques',
      noListings: 'Aucun logement disponible pour le moment',
      perMonth: '€/mois'
    },
    news: {
      title: 'Actualités & Événements',
      subtitle: 'Restez informé de nos dernières nouvelles',
      readMore: 'Lire la suite',
      noNews: 'Aucune actualité pour le moment',
      categories: {
        event: 'Événement',
        news: 'Actualité',
        announcement: 'Annonce'
      }
    },
    footer: {
      tagline: 'Ensemble pour une aide efficace, durable et au bon moment !',
      quickLinks: 'Liens Rapides',
      contactInfo: 'Contact',
      followUs: 'Suivez-nous',
      copyright: '© 2023 AILE. Tous droits réservés.'
    },
    admin: {
      dashboard: 'Tableau de bord',
      manageListings: 'Gérer les logements',
      manageNews: 'Gérer les actualités',
      contacts: 'Messages de contact',
      logout: 'Déconnexion',
      login: 'Connexion',
      email: 'Email',
      password: 'Mot de passe',
      loginButton: 'Se connecter',
      welcome: 'Bienvenue sur l\'interface d\'administration',
      addNew: 'Ajouter',
      edit: 'Modifier',
      delete: 'Supprimer',
      save: 'Enregistrer',
      cancel: 'Annuler',
      confirm: 'Confirmer la suppression',
      confirmText: 'Êtes-vous sûr de vouloir supprimer cet élément ?'
    }
  },
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      listings: 'Housing',
      news: 'News',
      about: 'About',
      contact: 'Contact',
      login: 'Admin Login'
    },
    hero: {
      title: 'Your partner for a fresh start in Lyon',
      subtitle: 'AILE helps international students find housing and integrate easily in France',
      cta: 'Discover our services',
      ctaSecondary: 'Contact us'
    },
    services: {
      title: 'Our Services',
      subtitle: 'Complete support for your success',
      housing: {
        title: 'Housing Search',
        description: 'We help you find the perfect accommodation suited to your needs and budget. Studio, 1-bedroom, 2-bedroom, or shared.'
      },
      admin: {
        title: 'Administrative Support',
        description: 'Complete assistance for your procedures: CAF, social security, prefecture, residence permit. We simplify everything.'
      },
      job: {
        title: 'Student Job Search',
        description: 'Personalized support to find a student job and facilitate your financial independence.'
      },
      integration: {
        title: 'Facilitate Integration',
        description: 'An interactive process to help you feel at home in Lyon, from arrival to the end of your studies.'
      }
    },
    about: {
      title: 'About AILE',
      mission: 'Our Mission',
      missionText: 'AILE is a non-profit association founded in 2019 to help international students in Lyon. We promote integration by offering personalized support in housing search and administrative procedures.',
      values: 'Our Values',
      proximity: 'Proximity',
      proximityText: 'Help adapted to your needs',
      solidarity: 'Solidarity',
      solidarityText: 'Active listening and strong relationships',
      love: 'Love',
      loveText: 'Sharing and mutual aid',
      respect: 'Respect',
      respectText: 'Regardless of nationality, religion or politics',
      pricing: 'Pricing',
      pricingText: 'One-time membership fee of €20 for complete access to all our services.'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Have a question? Need help? We are here for you.',
      name: 'Full name',
      email: 'Email',
      phone: 'Phone (optional)',
      subject: 'Subject',
      message: 'Your message',
      send: 'Send',
      sending: 'Sending...',
      success: 'Message sent successfully!',
      error: 'Error sending message',
      info: {
        title: 'Contact Information',
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
        addressValue: '1 place Abbé Pierre, 69009 Lyon'
      }
    },
    listings: {
      title: 'Available Housing',
      subtitle: 'Find your new home in Lyon',
      rent: 'Rent',
      deposit: 'Deposit',
      duration: 'Duration',
      available: 'Available from',
      features: 'Features',
      noListings: 'No housing available at the moment',
      perMonth: '€/month'
    },
    news: {
      title: 'News & Events',
      subtitle: 'Stay informed about our latest news',
      readMore: 'Read more',
      noNews: 'No news at the moment',
      categories: {
        event: 'Event',
        news: 'News',
        announcement: 'Announcement'
      }
    },
    footer: {
      tagline: 'Together for effective, sustainable and timely assistance!',
      quickLinks: 'Quick Links',
      contactInfo: 'Contact',
      followUs: 'Follow Us',
      copyright: '© 2025 AILE. All rights reserved.'
    },
    admin: {
      dashboard: 'Dashboard',
      manageListings: 'Manage Housing',
      manageNews: 'Manage News',
      contacts: 'Contact Messages',
      logout: 'Logout',
      login: 'Login',
      email: 'Email',
      password: 'Password',
      loginButton: 'Sign in',
      welcome: 'Welcome to the admin interface',
      addNew: 'Add New',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm deletion',
      confirmText: 'Are you sure you want to delete this item?'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    const saved = localStorage.getItem('language');
    if (saved && (saved === 'fr' || saved === 'en')) {
      setLanguage(saved);
    }
  }, []);

  const switchLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};